import { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import "./App.css";
import Footer from "./component/Footer/Footer";
import Navbar from "./component/Navbar/Navbar";
import ScrollToTop from "./ScrollToTop";
import Loader from "./component/Loader/Loader";

// ── Lazy-loaded route components (code-split into separate chunks) ──
const Homepage = lazy(() => import("./pages/Homepage"));
const Allpage = lazy(() => import("./pages/Allpage"));
const MergePDFTool = lazy(() => import("./component/Tools/MergePDFTool"));

const SelectPdf = lazy(() => import("./component/compress/SelectPDF"));
const PreviewPdf = lazy(() => import("./component/compress/PreviewPdf"));
const DownloadPdf = lazy(() => import("./component/compress/DownloadPDF"));

const UploadPdf = lazy(() => import("./component/crop/UploadPdf"));
const CropPdf = lazy(() => import("./component/crop/CropPdf"));
const Download = lazy(() => import("./component/crop/Download"));

const UploadSplit = lazy(() => import("./component/split/UploadSplit"));
const PreviewSplit = lazy(() => import("./component/split/PreviewSplit"));
const DownloadSplit = lazy(() => import("./component/split/DownloadSplit"));

const HighlightEditor = lazy(() => import("./component/Highlight/HighlightEditor"));
const PdfPreview = lazy(() => import("./component/Highlight/preview"));
const DownloadHighlight = lazy(() => import("./component/Highlight/DownloadHighlight"));

const UploadJpg = lazy(() => import("./component/Jpgtopdf/UploadJpg"));
const PreviewJpg = lazy(() => import("./component/Jpgtopdf/PreviewJpg"));
const DownloadJpg = lazy(() => import("./component/Jpgtopdf/DownloadJpg"));

const PdfToWordUpload = lazy(() => import("./component/PdfToWord/PdfToWordUpload"));
const PreviewPdfToWord = lazy(() => import("./component/PdfToWord/PreviewPdfToWord"));
const DownloadPdfToWord = lazy(() => import("./component/PdfToWord/DownloadPdfToWord"));

const PreviewEdit = lazy(() => import("./component/Editpdf/PreviewEdit"));
const EditPdf = lazy(() => import("./component/Editpdf/EditPdf"));
const DownloadEditedPdf = lazy(() => import("./component/Editpdf/DownloadEditPdf"));

const PrivacyPolicy = lazy(() => import("./component/policy/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./component/policy/TermsConditions"));

const AboutUs = lazy(() => import("./component/about/AboutUs"));
const ContactUs = lazy(() => import("./component/Contact/ContactUs"));

const AddPage = lazy(() => import("./component/Add Pages/AddPreview"));
const EditPages = lazy(() => import("./component/Add Pages/EditPages"));
const DownloadUpdates = lazy(() => import("./component/Add Pages/DownloadUpdates"));

const DeletePreview = lazy(() => import("./component/Delete Pages/DeletePreview"));
const DeletePages = lazy(() => import("./component/Delete Pages/DeletePages"));
const DownloadDeleted = lazy(() => import("./component/Delete Pages/DownloadDeleted"));

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleLoad = () => setIsLoading(false);

    if (document.readyState === "complete") {
      const timer = setTimeout(() => setIsLoading(false), 300);
      return () => clearTimeout(timer);
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  return (
    <>
      <Loader show={isLoading} />
      <Router>
        <Navbar />
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/Allpage" element={<Allpage />} />
            <Route path="/MergePDFTool" element={<MergePDFTool />} />

            {/* Compress PDF Page */}
            <Route path="/compress-pdf" element={<SelectPdf />} />
            <Route path="/preview" element={<PreviewPdf />} />
            <Route path="/download-pdf" element={<DownloadPdf />} />

            {/* Crop PDF Page */}
            <Route path="/upload-pdf" element={<UploadPdf />} />
            <Route path="/crop" element={<CropPdf />} />
            <Route path="/download" element={<Download />} />

            {/* highlight */}
            <Route path="/highlight-pdf" element={<PdfPreview />} />
            <Route path="/merge-pdf" element={<HighlightEditor />} />
            <Route path="/download-highlight" element={<DownloadHighlight />} />

            {/* split */}
            <Route path="/upload-split" element={<UploadSplit />} />
            <Route path="/preview-split" element={<PreviewSplit />} />
            <Route path="/download-split" element={<DownloadSplit />} />

            {/* JPG TO PDF */}
            <Route path="/upload-jpg" element={<UploadJpg />} />
            <Route path="/preview-jpg" element={<PreviewJpg />} />
            <Route path="/download-jpg" element={<DownloadJpg />} />

            {/* PDF TO WORD */}
            <Route path="/pdf-to-word" element={<PdfToWordUpload />} />
            <Route path="/preview-pdf-to-word" element={<PreviewPdfToWord />} />
            <Route path="/download-pdf-to-word" element={<DownloadPdfToWord />} />

            {/* edit pdf */}
            <Route path="/upload-edit" element={<PreviewEdit />} />
            <Route path="/edit-pdf" element={<EditPdf />} />
            <Route path="/download-editedpdf" element={<DownloadEditedPdf />} />

            {/* privacy policy */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />

            {/* about us */}
            <Route path="/about-us" element={<AboutUs />} />

            {/* contact us */}
            <Route path="/contact" element={<ContactUs />} />

            {/* Add Pages */}
            <Route path="/add-preview" element={<AddPage />} />
            <Route path="/edit-pages" element={<EditPages />} />
            <Route path="/download-updates" element={<DownloadUpdates />} />

            {/* Delete Pages */}
            <Route path="/delete" element={<DeletePreview />} />
            <Route path="/delete-pages" element={<DeletePages />} />
            <Route path="/download-deleted" element={<DownloadDeleted />} />

          </Routes>
        </Suspense>
        <Footer />
      </Router>
    </>
  );
}

export default App;
